import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import { useForm } from 'react-hook-form'
import { 
  Plus, 
  Search, 
  Filter, 
  Edit, 
  Trash2, 
  UserCog,
  UserCheck,
  UserX,
  Calendar,
  CreditCard
} from 'lucide-react'
// import { employeeAPI } from '../services/api' // Removed API calls
import { LoadingSpinner } from '../components'
import toast from 'react-hot-toast'

function Employees() {
  const [searchTerm, setSearchTerm] = useState('')
  const [currentPage, setCurrentPage] = useState(1)
  const [showModal, setShowModal] = useState(false)
  const [editingEmployee, setEditingEmployee] = useState(null)

  const queryClient = useQueryClient()

  // Fetch employees
  const { data: employeesData, isLoading } = useQuery(
    ['employees', { page: currentPage, search: searchTerm }],
    () => employeeAPI.getAll({
      page: currentPage,
      limit: 10,
      search: searchTerm,
    })
  )

  // Create employee mutation
  const createEmployeeMutation = useMutation(employeeAPI.create, {
    onSuccess: () => {
      queryClient.invalidateQueries('employees')
      setShowModal(false)
      toast.success('Employee created successfully!')
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Failed to create employee')
    },
  })

  // Update employee mutation
  const updateEmployeeMutation = useMutation(
    ({ id, data }) => employeeAPI.update(id, data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('employees')
        setShowModal(false)
        setEditingEmployee(null)
        toast.success('Employee updated successfully!')
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to update employee')
      },
    }
  )

  // Delete employee mutation
  const deleteEmployeeMutation = useMutation(employeeAPI.delete, {
    onSuccess: () => {
      queryClient.invalidateQueries('employees')
      toast.success('Employee deleted successfully!')
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Failed to delete employee')
    },
  })

  // Toggle status mutation
  const toggleStatusMutation = useMutation(employeeAPI.toggleStatus, {
    onSuccess: () => {
      queryClient.invalidateQueries('employees')
      toast.success('Employee status updated successfully!')
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Failed to update employee status')
    },
  })

  const { register, handleSubmit, reset, formState: { errors } } = useForm()

  const onSubmit = (data) => {
    if (editingEmployee) {
      updateEmployeeMutation.mutate({ id: editingEmployee.id, data })
    } else {
      createEmployeeMutation.mutate(data)
    }
  }

  const handleEdit = (employee) => {
    setEditingEmployee(employee)
    reset({
      firstName: employee.firstName,
      lastName: employee.lastName,
      phone: employee.phone,
      email: employee.email,
      address: employee.address,
      designation: employee.designation,
      skills: JSON.stringify(employee.skills, null, 2),
      joinDate: employee.joinDate,
      salary: employee.salary,
    })
    setShowModal(true)
  }

  const handleDelete = (employee) => {
    if (window.confirm('Are you sure you want to delete this employee?')) {
      deleteEmployeeMutation.mutate(employee.id)
    }
  }

  const handleToggleStatus = (employee) => {
    toggleStatusMutation.mutate(employee.id)
  }

  const handleCloseModal = () => {
    setShowModal(false)
    setEditingEmployee(null)
    reset()
  }

  const employees = employeesData?.data?.employees || []

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-4xl font-bold text-secondary-900">Employees</h1>
          <p className="mt-1 text-base text-secondary-600">
            Manage staff and employee information
          </p>
        </div>
        <button
          onClick={() => setShowModal(true)}
          className="btn-primary"
        >
          <Plus className="h-4 w-4 mr-2" />
          Add Employee
        </button>
      </div>

      {/* Filters */}
      <div className="card">
        <div className="card-content">
          <div className="flex items-center space-x-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-secondary-400" />
                <input
                  type="text"
                  placeholder="Search employees..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="input pl-10"
                />
              </div>
            </div>
            <button
              onClick={() => setSearchTerm('')}
              className="btn-outline"
            >
              <Filter className="h-4 w-4 mr-2" />
              Clear
            </button>
          </div>
        </div>
      </div>

      {/* Employees Table */}
      <div className="card">
        <div className="card-content p-0">
          {isLoading ? (
            <LoadingSpinner size="lg" className="h-64" />
          ) : (
            <div className="overflow-x-auto">
              <table className="table">
                <thead className="table-header">
                  <tr className="table-row">
                    <th className="table-head">Employee</th>
                    <th className="table-head">Designation</th>
                    <th className="table-head">Contact</th>
                    <th className="table-head">Join Date</th>
                    <th className="table-head">Salary</th>
                    <th className="table-head">Status</th>
                    <th className="table-head">Actions</th>
                  </tr>
                </thead>
                <tbody className="table-body">
                  {employees.map((employee) => (
                    <tr key={employee.id} className="table-row">
                      <td className="table-cell">
                        <div className="flex items-center">
                          <div className="h-8 w-8 bg-primary-100 rounded-full flex items-center justify-center">
                            <span className="text-sm font-medium text-primary-700">
                              {employee.firstName.charAt(0)}{employee.lastName.charAt(0)}
                            </span>
                          </div>
                          <div className="ml-3">
                            <div className="text-sm font-medium text-secondary-900">
                              {employee.firstName} {employee.lastName}
                            </div>
                            <div className="text-sm text-secondary-500">
                              {employee.skills?.join(', ') || 'No skills listed'}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="table-cell">
                        <div className="text-sm text-secondary-900">
                          {employee.designation}
                        </div>
                      </td>
                      <td className="table-cell">
                        <div className="text-sm text-secondary-900">{employee.phone}</div>
                        <div className="text-sm text-secondary-500">{employee.email}</div>
                      </td>
                      <td className="table-cell">
                        <div className="flex items-center text-sm text-secondary-900">
                          <Calendar className="h-4 w-4 mr-1" />
                          {new Date(employee.joinDate).toLocaleDateString()}
                        </div>
                      </td>
                      <td className="table-cell">
                        <div className="flex items-center text-sm text-secondary-900">
                          <CreditCard className="h-4 w-4 mr-1" />
                          ₹{employee.salary || 0}
                        </div>
                      </td>
                      <td className="table-cell">
                        <span className={`badge ${employee.isActive ? 'badge-success' : 'badge-danger'}`}>
                          {employee.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="table-cell">
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() => handleEdit(employee)}
                            className="text-primary-600 hover:text-primary-900"
                          >
                            <Edit className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleToggleStatus(employee)}
                            className={employee.isActive ? 'text-danger-600 hover:text-danger-900' : 'text-success-600 hover:text-success-900'}
                          >
                            {employee.isActive ? <UserX className="h-4 w-4" /> : <UserCheck className="h-4 w-4" />}
                          </button>
                          <button
                            onClick={() => handleDelete(employee)}
                            className="text-danger-600 hover:text-danger-900"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Employee Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-screen items-center justify-center p-4">
            <div className="modal-overlay" onClick={handleCloseModal} />
            <div className="modal-content max-w-2xl">
              <h3 className="text-lg font-semibold text-secondary-900 mb-4">
                {editingEmployee ? 'Edit Employee' : 'Add New Employee'}
              </h3>
              <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="label">First Name</label>
                    <input
                      {...register('firstName', { required: 'First name is required' })}
                      className="input"
                      placeholder="Enter first name"
                    />
                    {errors.firstName && (
                      <p className="form-error">{errors.firstName.message}</p>
                    )}
                  </div>
                  <div>
                    <label className="label">Last Name</label>
                    <input
                      {...register('lastName', { required: 'Last name is required' })}
                      className="input"
                      placeholder="Enter last name"
                    />
                    {errors.lastName && (
                      <p className="form-error">{errors.lastName.message}</p>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="label">Phone</label>
                    <input
                      {...register('phone', { required: 'Phone is required' })}
                      className="input"
                      placeholder="Enter phone number"
                    />
                    {errors.phone && (
                      <p className="form-error">{errors.phone.message}</p>
                    )}
                  </div>
                  <div>
                    <label className="label">Email</label>
                    <input
                      {...register('email', {
                        pattern: {
                          value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                          message: 'Invalid email address'
                        }
                      })}
                      type="email"
                      className="input"
                      placeholder="Enter email address"
                    />
                    {errors.email && (
                      <p className="form-error">{errors.email.message}</p>
                    )}
                  </div>
                </div>

                <div>
                  <label className="label">Address</label>
                  <textarea
                    {...register('address')}
                    className="input"
                    rows={2}
                    placeholder="Enter address"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="label">Designation</label>
                    <input
                      {...register('designation', { required: 'Designation is required' })}
                      className="input"
                      placeholder="Enter designation"
                    />
                    {errors.designation && (
                      <p className="form-error">{errors.designation.message}</p>
                    )}
                  </div>
                  <div>
                    <label className="label">Salary</label>
                    <input
                      {...register('salary')}
                      type="number"
                      step="0.01"
                      className="input"
                      placeholder="Enter salary"
                    />
                  </div>
                </div>

                <div>
                  <label className="label">Join Date</label>
                  <input
                    {...register('joinDate', { required: 'Join date is required' })}
                    type="date"
                    className="input"
                  />
                  {errors.joinDate && (
                    <p className="form-error">{errors.joinDate.message}</p>
                  )}
                </div>

                <div>
                  <label className="label">Skills (JSON Array)</label>
                  <textarea
                    {...register('skills')}
                    className="input font-mono text-sm"
                    rows={3}
                    placeholder='["Tailoring", "Cutting", "Stitching"]'
                  />
                  <p className="form-help">
                    Enter skills as JSON array. Example: ["Tailoring", "Cutting", "Stitching"]
                  </p>
                </div>

                <div className="flex justify-end space-x-3">
                  <button
                    type="button"
                    onClick={handleCloseModal}
                    className="btn-outline"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={createEmployeeMutation.isLoading || updateEmployeeMutation.isLoading}
                    className="btn-primary"
                  >
                    {createEmployeeMutation.isLoading || updateEmployeeMutation.isLoading ? (
                      <LoadingSpinner size="sm" className="mr-2" />
                    ) : null}
                    {editingEmployee ? 'Update Employee' : 'Create Employee'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default Employees
